/** Theme selection: explicit light, explicit dark, or follow the system. */
export type ThemePreference = "light" | "dark" | "system";

const STORAGE_KEY = "drumlin.theme";

/** Reads the stored preference, defaulting to following the system. */
export function readThemePreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    /* Private windows can deny storage; following the system is a fine default. */
  }
  return "system";
}

/** Applies a preference to the document and persists it. */
export function applyTheme(preference: ThemePreference): void {
  const resolved =
    preference === "system"
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light"
      : preference;

  document.documentElement.setAttribute("data-theme", resolved);

  try {
    if (preference === "system") localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    /* Not being able to remember the choice is not worth failing over. */
  }
}

/** Calls back when the system theme changes, while the preference is "system". */
export function watchSystemTheme(onChange: () => void): () => void {
  const media = window.matchMedia("(prefers-color-scheme: dark)");
  media.addEventListener("change", onChange);
  return () => media.removeEventListener("change", onChange);
}
